import { RefElement } from 'widgets/toolbox/RefElement';
import { extractPath } from 'core/utils';

const ARIA_HIDDEN = 'aria-hidden';

/**
 * @description Mixin for Base AccordionItem implementation
 * @param AccordionItem base widget class
 * @returns AccordionItem widget
 */
function CoreAccordionItemMixinClassCreator(AccordionItem: import('widgets/global/AccordionItem').TAccordionItem) {
    /**
     * @category widgets
     * @subcategory global
     * @class CoreAccordionItemMixin
     * @augments AccordionItem
     * @classdesc Represents AccordionItem with specific logic for accordion item, handling viewtype changes, keyboard navigation
     * Represents AccordionItem component with next features:
     * 1. Configurable via data attribute expanded state
     * 2. Support keyboard navigation for accessibility
     * 3. Define accessibility
     * @property {string} data-widget - Widget name `accordionItem`
     * @property {string} data-event-keydown - Event listener for `handleKeydown` method
     * @property {string} data-event-click - Event listener for `togglePanel` method
     * @property {string} data-widget-event-accordionitemupdate - method to be triggered on parent's widget to update current accordion item
     * @property {boolean} [data-expanded=false] - Expanded flag
     * @example
     * // use this code to display widget
     *   <section
     *      class="b-product_accordion-item"
     *      data-widget="accordionItem"
     *      data-widget-event-closeallitems="closeItems"
     *      data-widget-event-accordionitemupdate="updateFocusedItem"
     *  >
     *      <button
     *          class="b-refinements_accordion-button"
     *          data-ref="accordionItemBtn"
     *          data-event-click.prevent="togglePanel"
     *          data-event-keydown="handleKeydown"
     *          data-event-focus="handleFocus"
     *          type="button"
     *      >
     *          <span class="b-refinements_accordion-title">Account</span>
     *          <span class="b-icon_chevron"></span>
     *      </button>
     *      <div data-ref="accordionItemPanel">
     *          <div data-ref="accordionItemPanelInner">
     *              ... account panel content
     *          </div>
     *      </div>
     *  </section>
     */

    class CoreAccordionItemMixin extends AccordionItem {
        prefs() {
            return {
                showMoreClassName: 'm-show_more',
                parentNameClass: 'b-footer_nav-heading',
                showMoreBtn: 'showMoreBtn',
                showLessBtn: 'showLessBtn',
                ...super.prefs()
            };
        }

        /**
         * @description Initialize widget logic
         */
        init() {
            super.init();
            this.eventBus().on('refinement.tab.activated', 'cleanPanelHeight');
            this.eventBus().on('update.accordion.item.height', 'updateState');
            this.eventBus().on('inputstatechanged', 'updateState');
        }

        /**
         * @description Toggle panel height, so it could be properly animated.
         * It is required inner element to has content height properly.
         * @param isOpen Panel Open flag
         * @param panel IsOpen flag
         */

        /**
         * @description Resize Observer to track Yotpo Reviews 3d Party content height inside of Product Tab
         */

        private resizeObserver = new ResizeObserver(entries => {
            entries.forEach(entry => {
                const innerElement = entry.target as HTMLElement;
                const panelElement = document.querySelector<HTMLElement>('#product-reviews-btn-1');

                if (panelElement) {
                    panelElement.style.height = `${innerElement.offsetHeight}px`;
                }
            });
        });

        togglePanelHeight(isOpen, panel) {
            panel.attr(ARIA_HIDDEN, isOpen ? 'false' : 'true');

            this.has(this.prefs().accordionItemPanelInner, inner => {
                const panelElement = panel.get();
                const innerElement = inner.get();

                if (this.prefs().animateToggle && innerElement && panelElement && panelElement.id === 'product-reviews-btn-1') {
                    // Preload all images inside innerElement
                    this.preloadImages(innerElement);

                    if (isOpen) {
                        panelElement.style.height = `${innerElement.offsetHeight}px`;
                        this.resizeObserver.observe(innerElement);
                    } else {
                        panelElement.style.height = `0px`;
                        this.resizeObserver.unobserve(innerElement);
                    }
                }
            });

            this.eventBus().emit('toggle.accordion.item');
        }

        handleShowMore() {
            this.ref('self').toggleClass(this.prefs().showMoreClassName);
            this.updateState();

            if (this.ref('self').get()?.classList.contains(this.prefs().showMoreClassName)) {
                this.has(this.prefs().showLessBtn, showLessBtn => {
                    showLessBtn.get()?.focus();
                });
            } else {
                this.has(this.prefs().showMoreBtn, showMoreBtn => {
                    showMoreBtn.get()?.focus();
                });
            }
        }

        /**
         * Preloads all images within the specified DOM element that have not yet finished loading.
         *
         * This method searches for all `<img>` elements inside the provided `innerElement`.
         * For each image that is not yet complete, it creates a new `Image` object and sets its `src`
         * attribute to the same source as the original image, triggering the browser to preload it.
         *
         * @param innerElement - The DOM element containing images to preload.
         */
        preloadImages(innerElement) {
            const images = innerElement.querySelectorAll('img');

            images.forEach(img => {
                if (img.complete) {
                    return;
                }

                const src = img.getAttribute('src');

                if (src) {
                    const preloadImg = new Image();

                    preloadImg.src = src;
                }
            });
        }

        /**
         * @description Track click on footer menu links
         */
        menuFooterClick(menuItem: RefElement<HTMLElement>) {
            const url = extractPath(menuItem.attr('href'));
            const parentNameElement = this.ref('self').get()?.getElementsByClassName(this.prefs().parentNameClass)?.[0];
            const parentName = parentNameElement?.textContent ? parentNameElement.textContent.replace(/\n/g, ' ').trim() : '';

            this.eventBus().emit('footer.click', url, parentName);
        }
    }

    return CoreAccordionItemMixin;
}

export type TCoreAccordionItemMixin = ReturnType<typeof CoreAccordionItemMixinClassCreator>;

export type TCoreAccordionItemMixinInstance = InstanceType<TCoreAccordionItemMixin>;

export default CoreAccordionItemMixinClassCreator;
